import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { REPORTING_LOGIN } from 'src/api';
import axios from 'src/axios';

export type ReportingInitialState = {
  currentUser: {
    session: {
      id: string;
      token: string;
    } | null;
    account: {
      id: string;
      notificationsEnabled: boolean;
    } | null;
    user: {
      id: number;
      userHash: string;
      name: string;
      hasPreviouslyConnectedSlackAccount?: boolean;
    } | null;
    organisation: {
      id: string;
      name: string;
    } | null;
  };
  createReportingSessionLoading: boolean;
  fetchReportingSessionLoading: boolean;
};

const initialState: ReportingInitialState = {
  currentUser: {
    session: null,
    account: null,
    user: null,
    organisation: null,
  },
  createReportingSessionLoading: false,
  fetchReportingSessionLoading: false,
};

interface CreateReportingSessionResponse {
  id: string;
  token: string;
  account: {
    id: string;
    notificationsEnabled: boolean;
    user: {
      id: number;
      name: string;
      userHash: string;
      hasPreviouslyConnectedSlackAccount?: boolean;
      organisation: {
        id: string;
        name: string;
      };
    };
  };
}
// Thunks
export const createReportingSession = createAsyncThunk<
  CreateReportingSessionResponse,
  { username: string; password: string },
  { rejectValue: { errors: { code?: string; message: string }[] } }
>(
  'reporting/createReportingSession',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post<{
        data: CreateReportingSessionResponse;
      }>(REPORTING_LOGIN, {
        username,
        password,
      });

      return data.data;
    } catch (error) {
      const errorResponse = error as AxiosError<{
        errors: { code?: string; message: string }[];
      }>;
      if (errorResponse) {
        throw rejectWithValue(
          errorResponse.response?.data ?? {
            errors: [{ message: 'Unknown error' }],
          },
        );
      }
      throw error;
    }
  },
);

export const fetchReportingSession =
  createAsyncThunk<CreateReportingSessionResponse>(
    'reporting/fetchReportingSession',
    async () => {
      const {
        data: { data: session },
      } = await axios.get<{ data: CreateReportingSessionResponse }>(
        REPORTING_LOGIN,
      );

      return session;
    },
  );

export const reportingSlice = createSlice({
  name: 'reporting',
  initialState,

  reducers: {
    loginSuccessful: (
      state,
      action: {
        payload: {
          id: string;
          token: string;
          account: {
            id: string;
            notificationsEnabled: boolean;
            user: {
              id: number;
              userHash: string;
              name: string;
              hasPreviouslyConnectedToSlack?: boolean;
            };
          };
          organisation: {
            id: string;
            name: string;
          };
        };
      },
    ) => {
      state.currentUser = {
        ...state.currentUser,
        session: {
          id: action.payload.id,
          token: action.payload.token,
        },
        account: {
          id: action.payload.account.id,
          notificationsEnabled: action.payload.account.notificationsEnabled,
        },
        organisation: {
          id: action.payload.organisation.id,
          name: action.payload.organisation.name,
        },
        user: {
          ...(action.payload.account.user ?? {}),
        },
      };
    },
    logoutSuccessful: (state) => {
      state.currentUser = {
        session: null,
        account: null,
        user: null,
        organisation: null,
      };
      state.createReportingSessionLoading = false;
      state.fetchReportingSessionLoading = false;
    },
  },

  selectors: {
    getCurrentUser: (state: ReportingInitialState) => state.currentUser,
    getCreateReportingSessionLoading: (state: ReportingInitialState) =>
      state.createReportingSessionLoading,
    getFetchReportingSessionLoading: (state: ReportingInitialState) =>
      state.fetchReportingSessionLoading,
  },

  extraReducers: (builder) => {
    builder.addCase(createReportingSession.pending, (state) => {
      state.createReportingSessionLoading = true;
    });
    builder.addCase(createReportingSession.fulfilled, (state, action) => {
      state.createReportingSessionLoading = false;

      state.currentUser = {
        ...state.currentUser,
        session: {
          id: action.payload.id,
          token: action.payload.token,
        },
        account: {
          id: action.payload.account.id,
          notificationsEnabled: action.payload.account.notificationsEnabled,
        },
        organisation: {
          id: action.payload.account.user.organisation.id,
          name: action.payload.account.user.organisation.name,
        },
        user: {
          ...(action.payload.account.user ?? {}),
        },
      };
    });
    builder.addCase(createReportingSession.rejected, (state) => {
      state.createReportingSessionLoading = false;
    });

    builder.addCase(fetchReportingSession.pending, (state) => {
      state.fetchReportingSessionLoading = true;
    });
    builder.addCase(fetchReportingSession.fulfilled, (state, action) => {
      state.fetchReportingSessionLoading = false;

      state.currentUser = {
        ...state.currentUser,
        session: {
          id: action.payload.id,
          token: action.payload.token,
        },
        account: {
          id: action.payload.account.id,
          notificationsEnabled: action.payload.account.notificationsEnabled,
        },
        organisation: {
          id: action.payload.account.user.organisation.id,
          name: action.payload.account.user.organisation.name,
        },
        user: {
          ...(action.payload.account.user ?? {}),
        },
      };
    });
    builder.addCase(fetchReportingSession.rejected, (state) => {
      state.fetchReportingSessionLoading = false;
    });
  },
});

export const { loginSuccessful, logoutSuccessful } = reportingSlice.actions;
export const {
  getCurrentUser,
  getCreateReportingSessionLoading,
  getFetchReportingSessionLoading,
} = reportingSlice.selectors;

export const reportingReducer = reportingSlice.reducer;
